import {isFeatureEnabled} from '@github-ui/feature-flags'
import {searchUrl} from '@github-ui/issue-url-helper'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {QUERIES} from '@github-ui/issue-url-helper/known-queries'
import {replaceTokensDifferentially} from '@github-ui/issues-react/utils/urls'
import {LabelToken} from '@github-ui/label-token'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemActionBar} from '@github-ui/list-view/ListItemActionBar'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {Link as RouterLink} from '@github-ui/react-router'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ActionList, Banner, Button, Dialog, Link} from '@primer/react'
import {AriaAlert} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from './constants/labels'
import {DialogForm as EditDialog} from './DialogForm'
import {IssuesAndPullRequestsCount} from './IssuesAndPullRequestsCount'
import {commitDeleteLabelMutation} from './mutations/delete-label-mutation'
import {commitUpdateLabelMutation} from './mutations/update-label-mutation'
import type {LabelNode} from './repository-label-index-page-query-types'
import styles from './RepositoryLabel.module.css'
import {formatFormErrorMessage} from './utils'

// we can export it later when we implement fragment-checks.ts for unused field detection
type LabelRowFragment = Pick<LabelNode, 'id' | 'name' | 'nameHTML' | 'color' | 'description'>

type LabelRowProps = {
  label: LabelRowFragment
  isActionsAvailable: boolean
  viewerCanPush: boolean
  labelIds: string[]
  repositoryId: string
  repositoryNameWithOwner: string
}

export function LabelRow(props: LabelRowProps) {
  const dataRouterEnabled = isFeatureEnabled('issues_data_router')

  if (dataRouterEnabled) {
    return <LabelRowNew {...props} />
  }

  return <LabelRowRelay {...props} />
}

function LabelRowNew({label, isActionsAvailable, viewerCanPush, labelIds, repositoryNameWithOwner}: LabelRowProps) {
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')
  const [errorsPresent, setErrorsPresent] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editSubmissionErrors, setEditSubmissionErrors] = useState('')

  const errorBanner = useRef<HTMLDivElement>(null)
  const listItemRef = useRef<HTMLLIElement>(null)
  const returnFocusRef = useRef<HTMLButtonElement>(null)

  const handleDelete = useCallback(() => {
    setIsDialogOpen(false)
  }, [])

  const handleEdit = useCallback(
    (
      _input: {
        name: string
        description: string
        color: string
      },
      setIsSubmitting: (isSubmitting: boolean) => void,
    ) => {
      setEditSubmissionErrors('')

      setIsSubmitting(false)
    },
    [],
  )

  useEffect(() => {
    if (errorsPresent && errorBanner?.current) {
      errorBanner.current.focus()
    }
  }, [errorBanner, errorsPresent])

  const newQuery = replaceTokensDifferentially(QUERIES.open, [label.name], 'label')
  const labelLink = searchUrl({viewId: VIEW_IDS.repository, query: newQuery})

  const captureReturnFocusRef = () => {
    const trigger = listItemRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')
    if (trigger) {
      returnFocusRef.current = trigger
    }
  }

  return (
    <LabelRowContent
      label={label}
      isActionsAvailable={isActionsAvailable}
      viewerCanPush={viewerCanPush}
      labelIds={labelIds}
      repositoryNameWithOwner={repositoryNameWithOwner}
      pinnedViewsEnabled={pinnedViewsEnabled}
      errorsPresent={errorsPresent}
      setErrorsPresent={setErrorsPresent}
      isDialogOpen={isDialogOpen}
      setIsDialogOpen={setIsDialogOpen}
      isEditDialogOpen={isEditDialogOpen}
      setIsEditDialogOpen={setIsEditDialogOpen}
      editSubmissionErrors={editSubmissionErrors}
      setEditSubmissionErrors={setEditSubmissionErrors}
      errorBanner={errorBanner}
      listItemRef={listItemRef}
      returnFocusRef={returnFocusRef}
      handleDelete={handleDelete}
      handleEdit={handleEdit}
      labelLink={labelLink}
      captureReturnFocusRef={captureReturnFocusRef}
    />
  )
}

function LabelRowRelay({
  label,
  isActionsAvailable,
  viewerCanPush,
  labelIds,
  repositoryId,
  repositoryNameWithOwner,
}: LabelRowProps) {
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')
  const [errorsPresent, setErrorsPresent] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editSubmissionErrors, setEditSubmissionErrors] = useState('')

  const errorBanner = useRef<HTMLDivElement>(null)
  const listItemRef = useRef<HTMLLIElement>(null)
  const returnFocusRef = useRef<HTMLButtonElement>(null)

  const environment = useRelayEnvironment()
  const handleDelete = useCallback(async () => {
    commitDeleteLabelMutation({
      environment,
      input: {id: label.id},
      onCompleted: () => {
        setErrorsPresent(false)
        setIsDialogOpen(false)

        // Upate the label counter in the header
        environment.commitUpdate(store => {
          const repo = store.get(repositoryId)
          if (!repo) return
          const labelsConnection = repo.getLinkedRecord('labels', {
            first: 30,
            orderBy: {direction: 'ASC', field: 'NAME'},
            skip: 0,
          })
          if (labelsConnection) {
            const totalCount = labelsConnection.getValue('totalCount') as number
            labelsConnection.setValue(Math.max(totalCount - 1, 0), 'totalCount')
          }
        })
      },
      onError: () => {
        setErrorsPresent(true)
      },
    })
  }, [label.id, repositoryId, environment])

  const handleEdit = useCallback(
    async (
      input: {
        name: string
        description: string
        color: string
      },
      setIsSubmitting: (isSubmitting: boolean) => void,
    ) => {
      setEditSubmissionErrors('')

      commitUpdateLabelMutation({
        environment,
        input: {
          id: label.id,
          name: input.name,
          description: input.description,
          color: input.color,
        },
        onCompleted: response => {
          if (!response.updateLabel?.label) {
            setEditSubmissionErrors(LABELS.editLabelError)
            setIsSubmitting(false)
            return
          }
          setIsSubmitting(false)
          setIsEditDialogOpen(false)
        },
        onError: (error: Error) => {
          if (error.cause && Array.isArray(error.cause)) {
            setEditSubmissionErrors(formatFormErrorMessage(error.cause))
          } else {
            setEditSubmissionErrors(LABELS.editLabelError)
          }
          setIsSubmitting(false)
        },
      })
    },
    [setEditSubmissionErrors, environment, label],
  )

  useEffect(() => {
    if (errorsPresent && errorBanner?.current) {
      errorBanner.current.focus()
    }
  }, [errorBanner, errorsPresent])

  const newQuery = replaceTokensDifferentially(QUERIES.open, [label.name], 'label')
  const labelLink = searchUrl({viewId: VIEW_IDS.repository, query: newQuery})

  const captureReturnFocusRef = () => {
    const trigger = listItemRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')
    if (trigger) {
      returnFocusRef.current = trigger
    }
  }

  return (
    <LabelRowContent
      label={label}
      isActionsAvailable={isActionsAvailable}
      viewerCanPush={viewerCanPush}
      labelIds={labelIds}
      repositoryNameWithOwner={repositoryNameWithOwner}
      pinnedViewsEnabled={pinnedViewsEnabled}
      errorsPresent={errorsPresent}
      setErrorsPresent={setErrorsPresent}
      isDialogOpen={isDialogOpen}
      setIsDialogOpen={setIsDialogOpen}
      isEditDialogOpen={isEditDialogOpen}
      setIsEditDialogOpen={setIsEditDialogOpen}
      editSubmissionErrors={editSubmissionErrors}
      setEditSubmissionErrors={setEditSubmissionErrors}
      errorBanner={errorBanner}
      listItemRef={listItemRef}
      returnFocusRef={returnFocusRef}
      handleDelete={handleDelete}
      handleEdit={handleEdit}
      labelLink={labelLink}
      captureReturnFocusRef={captureReturnFocusRef}
    />
  )
}

type LabelRowContentProps = {
  label: LabelRowFragment
  isActionsAvailable: boolean
  viewerCanPush: boolean
  labelIds: string[]
  repositoryNameWithOwner: string
  pinnedViewsEnabled: boolean
  errorsPresent: boolean
  setErrorsPresent: (v: boolean) => void
  isDialogOpen: boolean
  setIsDialogOpen: (v: boolean) => void
  isEditDialogOpen: boolean
  setIsEditDialogOpen: (v: boolean) => void
  editSubmissionErrors: string
  setEditSubmissionErrors: (v: string) => void
  errorBanner: React.RefObject<HTMLDivElement | null>
  listItemRef: React.RefObject<HTMLLIElement | null>
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  handleDelete: () => void
  handleEdit: (input: {name: string; description: string; color: string}, setIsSubmitting: (v: boolean) => void) => void
  labelLink: string
  captureReturnFocusRef: () => void
}

function LabelRowContent({
  label,
  isActionsAvailable,
  viewerCanPush,
  labelIds,
  repositoryNameWithOwner,
  pinnedViewsEnabled,
  errorsPresent,
  setErrorsPresent,
  isDialogOpen,
  setIsDialogOpen,
  isEditDialogOpen,
  setIsEditDialogOpen,
  editSubmissionErrors,
  setEditSubmissionErrors,
  errorBanner,
  listItemRef,
  returnFocusRef,
  handleDelete,
  handleEdit,
  labelLink,
  captureReturnFocusRef,
}: LabelRowContentProps) {
  return (
    <ListItem
      key={label.id}
      ref={listItemRef}
      title={<></>} // We need to solve this. title is important for announcing the list item but we don't have a title concept here.
      role="listitem"
      secondaryActions={
        isActionsAvailable ? (
          <ListItemActionBar
            staticMenuActions={[
              ...(viewerCanPush
                ? [
                    {
                      key: 'edit',
                      render: () => (
                        <ActionList.Item
                          onSelect={() => {
                            captureReturnFocusRef()
                            setIsEditDialogOpen(true)
                          }}
                        >
                          Edit
                        </ActionList.Item>
                      ),
                    },
                  ]
                : []),
              ...(viewerCanPush
                ? [
                    {
                      key: 'delete',
                      render: () => (
                        <>
                          <ActionList.Divider />
                          <ActionList.Item
                            variant="danger"
                            onSelect={() => {
                              captureReturnFocusRef()
                              setIsDialogOpen(true)
                            }}
                          >
                            {LABELS.deleteButtonText}
                          </ActionList.Item>
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        ) : undefined
      }
    >
      <ListItemMainContent>
        <ListItemDescription className={clsx(styles.labelRowDescription, !isActionsAvailable && styles.noActionBar)}>
          <ListItemDescriptionItem>
            {pinnedViewsEnabled ? (
              <RouterLink to={labelLink} aria-label={label.name}>
                <LabelToken
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
                  text={<VerifiedHTMLText html={label.nameHTML as SafeHTMLString} />}
                  interactive
                  key={0}
                  fillColor={`#${label.color}`}
                />
              </RouterLink>
            ) : (
              <Link href={labelLink} muted aria-label={label.name}>
                <LabelToken
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
                  text={<VerifiedHTMLText html={label.nameHTML as SafeHTMLString} />}
                  interactive
                  key={0}
                  fillColor={`#${label.color}`}
                />
              </Link>
            )}{' '}
          </ListItemDescriptionItem>
          <ListItemDescriptionItem className={styles.labelRowDescriptionItemDescription}>
            {label.description}
          </ListItemDescriptionItem>
          <IssuesAndPullRequestsCount
            repositoryNameWithOwner={repositoryNameWithOwner}
            labelName={label.name}
            labelId={label.id}
            labelIds={labelIds}
          />
        </ListItemDescription>
      </ListItemMainContent>
      {isDialogOpen && (
        <Dialog
          title={LABELS.labelDeleteDialogTitle}
          onClose={() => {
            setErrorsPresent(false)
            setIsDialogOpen(false)
          }}
          returnFocusRef={returnFocusRef}
        >
          <Dialog.Body>
            {errorsPresent && (
              <Banner
                ref={errorBanner}
                className="tmp-mb-3"
                title="Error"
                description={<AriaAlert>{LABELS.deleteLabelError}</AriaAlert>}
                variant="critical"
                role="alert"
              />
            )}
            <p>{LABELS.deleteLabelWarningPermanent}</p>
            <p>{LABELS.deleteLabelAssociatedIssuesNote}</p>
          </Dialog.Body>
          <Dialog.Footer>
            <Button
              onClick={() => {
                setErrorsPresent(false)
                setIsDialogOpen(false)
              }}
            >
              {LABELS.cancelButtonText}
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              {LABELS.deleteLabelButtonText}
            </Button>
          </Dialog.Footer>
        </Dialog>
      )}
      {isEditDialogOpen && (
        <EditDialog
          onDialogClose={() => {
            setEditSubmissionErrors('')
            setIsEditDialogOpen(false)
          }}
          formTitle={LABELS.editLabel}
          descriptionPlaceholder={LABELS.labelDescriptionPlaceholder}
          submissionErrors={editSubmissionErrors}
          submitButtonText={LABELS.saveChanges}
          onDialogSubmit={handleEdit}
          returnFocusRef={returnFocusRef}
          initialValues={{
            name: label.name,
            description: label.description,
            color: label.color,
          }}
        />
      )}
    </ListItem>
  )
}
